import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { toast } from 'sonner'
import { useAuth } from '@/lib/contexts/AuthContext'
import { useSubscription } from '@/lib/contexts/SubscriptionContext'
import { useTrialUsageStats } from '@/hooks/useTrialUsageStats'
import { usePlanUsage } from '@/hooks/usePlanUsage'
import {
  calculateTrialHoursLeft,
  getPaymentLink,
  getPlanById,
  getPlanDisplayName,
  getTrialPhase,
  getTrialPhaseCopy,
  type TrialPhase,
} from '@/lib/constants/pricing'
import { isPaymentRecoveryStatus, needsInvoicePortal } from '@/lib/utils/subscriptionEntitlement'
import { track } from '@/lib/utils/track'
import { ChipGlyph, ChipMeter, UrgencyBanner } from './plan-usage/UrgencyBanner'
import { EventQuotaInterrupt } from './plan-usage/EventQuotaInterrupt'
import { buildPaymentFailedCopy, buildReadOnlyCopy, creationNounForPath } from './plan-usage/accountBannerCopy'
import { buildQuotaInterruptCopy, buildQuotaWarningCopy, getQuotaLevel } from './plan-usage/eventQuota'

interface TrialBannerProps {
  className?: string
}

const JUST_STARTED_SESSION_KEY = 'humblytics_trial_just_started_dismissed'
const PHASE_DISMISS_KEY = (phase: TrialPhase) => `humblytics_trial_banner_dismissed_${phase}`
const PAYMENT_FAILED_DISMISS_KEY = 'humblytics_banner_dismissed_payment_failed'
const quotaDismissKey = (d: Date) => `humblytics_banner_dismissed_quota_${d.getFullYear()}_${d.getMonth() + 1}`

/** Amber reshows after 12h (DESIGN.md Urgency Scale). */
const AMBER_RESHOW_HOURS = 12

function readDismissed(key: string): boolean {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return false
    const expires = parseInt(raw, 10)
    if (!Number.isFinite(expires) || Date.now() > expires) {
      localStorage.removeItem(key)
      return false
    }
    return true
  } catch {
    return false
  }
}

function writeDismissed(key: string, hours: number) {
  try {
    if (hours <= 0) return
    localStorage.setItem(key, String(Date.now() + hours * 60 * 60 * 1000))
  } catch {
    // ignore
  }
}

/**
 * The account-state strip under the shell header. One strip at a time, in
 * order of what blocks the user most:
 *
 *  read-only after cancel (ink) → payment failed (amber) → trial expired (ink)
 *  → trial phases (forest / slate / amber ≤3 days / rust last 24h)
 *  → data points 100% (ink interrupt over the page) → data points 75% (amber)
 *
 * Paper "03 - App · Paywall & billing". Urgency earns its colour.
 */
export function TrialBanner({ className }: TrialBannerProps) {
  const { user, token } = useAuth()
  const location = useLocation()
  const {
    hasActiveTrial,
    trialDaysLeft,
    trialEndsAt,
    planName,
    currentPlan,
    isApproachingLimit,
    isOverLimit,
    shouldShowTrialBanner,
    hasActiveSubscription,
    usedTrial,
    isReadOnly,
    readOnlyReason,
    subscriptionStatus,
    stripeSubscriptionId,
    eventsUsed,
    eventLimit,
    handleUpgrade,
    openPricingModal,
    openUpdatePortal,
  } = useSubscription()
  const stats = useTrialUsageStats()
  const usage = usePlanUsage()

  // A trial that has just expired (clock ticked past trialEndsAt) is no longer
  // "active" from the context's perspective, but we still need to render the
  // ink loss-aversion banner. Compute locally so the banner is self-sufficient.
  const isTrialExpired = useMemo(() => {
    if (hasActiveSubscription) return false
    if (hasActiveTrial) return false
    // A lapsed trial is view-only too, but it keeps the "Tracking paused" strip.
    if (isReadOnly && readOnlyReason !== 'trial_ended') return false
    if (!usedTrial) return false
    // Older accounts (planSource: expired_trial) never stored trialEndsAt — treat as expired.
    if (!trialEndsAt) return true
    const ends = trialEndsAt instanceof Date ? trialEndsAt : new Date(trialEndsAt)
    return Number.isFinite(ends.getTime()) && ends.getTime() < Date.now()
  }, [hasActiveSubscription, hasActiveTrial, isReadOnly, readOnlyReason, usedTrial, trialEndsAt])

  // Tick once a minute so the final_24h countdown + expired transition stay current.
  const [, setTick] = useState(0)
  useEffect(() => {
    if (!hasActiveTrial && !isTrialExpired) return
    const id = setInterval(() => setTick(t => t + 1), 60_000)
    return () => clearInterval(id)
  }, [hasActiveTrial, isTrialExpired])

  // Auto-fade for the just_started phase (4s).
  const [hideJustStarted, setHideJustStarted] = useState(false)

  // Session-scoped "just activated" flag — only true on the activation render.
  const justActivatedThisSession = useMemo(() => {
    if (!hasActiveTrial) return false
    try {
      return sessionStorage.getItem(JUST_STARTED_SESSION_KEY) !== 'dismissed'
    } catch {
      return false
    }
  }, [hasActiveTrial])

  const hoursLeft = calculateTrialHoursLeft(trialEndsAt)
  const phase: TrialPhase | null = useMemo(() => {
    if (isTrialExpired) return 'expired'
    if (!hasActiveTrial) return null
    return getTrialPhase(trialDaysLeft, hoursLeft, justActivatedThisSession && !hideJustStarted)
  }, [isTrialExpired, hasActiveTrial, trialDaysLeft, hoursLeft, justActivatedThisSession, hideJustStarted])

  // Every trial and resubscribe CTA points at Business ($79/mo); Scale stays
  // Scale. Plus is never offered (retired from every paywall CTA).
  const targetPlan = useMemo(
    () => getPlanById(currentPlan === 'scale' ? 'scale' : 'business'),
    [currentPlan],
  )
  const monthlyPrice = targetPlan?.monthlyPrice ?? 79

  // Self-fade timer for just_started.
  useEffect(() => {
    if (phase !== 'just_started') return
    const id = window.setTimeout(() => {
      setHideJustStarted(true)
      try {
        sessionStorage.setItem(JUST_STARTED_SESSION_KEY, 'dismissed')
      } catch {
        // ignore
      }
    }, 4000)
    return () => window.clearTimeout(id)
  }, [phase])

  const goToCheckout = (trigger: string) => {
    if (!targetPlan) return
    // Monthly link: the button names the monthly price.
    track('stripe_checkout_initiated', token, {
      plan: targetPlan.id,
      billing: 'monthly',
      is_trial: !usedTrial,
      trigger,
    })
    window.location.href = getPaymentLink(targetPlan, false, usedTrial, user?.email, user?.userId)
  }

  const openPortal = async () => {
    const ok = await openUpdatePortal()
    if (!ok) toast.error("Couldn't open Stripe billing. Try again in a moment.")
  }

  const dismiss = (key: string) => {
    writeDismissed(key, AMBER_RESHOW_HOURS)
    setTick(t => t + 1)
  }

  const displayPlanName = getPlanDisplayName(currentPlan, planName)

  // 1. Read-only after cancel: data stays readable, creation needs a plan.
  //    A lapsed trial falls through to the expired-trial strip below.
  if (isReadOnly && readOnlyReason !== 'trial_ended') {
    const needsPayment = needsInvoicePortal(subscriptionStatus, stripeSubscriptionId)
    const copy = buildReadOnlyCopy({
      planName: displayPlanName,
      noun: creationNounForPath(location.pathname),
      needsPayment,
      monthlyPrice,
    })
    return (
      <UrgencyBanner
        tone="ink"
        chip={copy.chip}
        chipIcon={<ChipGlyph kind="lock" />}
        chipClassName="bg-white/10 font-medium"
        message={copy.message}
        ctaSize="md"
        cta={{
          label: copy.ctaLabel,
          // `unpaid` → invoice portal (openPricingModal routes it); canceled → a new checkout.
          onClick: () => (needsPayment ? openPricingModal() : goToCheckout('read_only_banner')),
        }}
        className={className}
      />
    )
  }

  // 2. Payment failed: Stripe is still retrying, access continues.
  if (isPaymentRecoveryStatus(subscriptionStatus)) {
    if (readDismissed(PAYMENT_FAILED_DISMISS_KEY)) return null
    const copy = buildPaymentFailedCopy({})
    return (
      <UrgencyBanner
        tone="amber"
        chip={copy.chip}
        chipIcon={<ChipGlyph kind="card" />}
        message={copy.message}
        hint={copy.hint}
        cta={{ label: copy.ctaLabel, onClick: () => void openPortal() }}
        onDismiss={() => dismiss(PAYMENT_FAILED_DISMISS_KEY)}
        className={className}
      />
    )
  }

  // 3–4. Trial phases, including the expired ink strip.
  if (phase && (isTrialExpired || (shouldShowTrialBanner && !(hasActiveSubscription && !isOverLimit && !isApproachingLimit)))) {
    // Phase-specific dismissal check (per-phase, so dismissing ambient
    // doesn't suppress rust later). Blocking phases skip this entirely.
    const isPhaseDismissed =
      phase !== 'just_started' &&
      phase !== 'final_24h' &&
      phase !== 'expired' &&
      readDismissed(PHASE_DISMISS_KEY(phase))
    if (isPhaseDismissed) return null

    const copy = getTrialPhaseCopy(phase, stats, {
      planName: targetPlan?.name || planName || 'Business',
      trialEndsAt,
      monthlyPrice,
      daysLeft: trialDaysLeft,
      hoursLeft,
    })
    const tone = phase === 'final_24h' ? 'rust' : phase === 'expired' ? 'ink' : 'amber'
    const canDismiss = !copy.blocking && phase !== 'just_started'

    return (
      <UrgencyBanner
        tone={tone}
        chip={copy.eyebrow ?? ''}
        chipIcon={phase === 'expired' ? undefined : <ChipGlyph kind="clock" />}
        message={copy.message}
        cta={{
          label: copy.ctaText,
          onClick: () => (copy.ctaSkipModal ? goToCheckout(`trial_${phase}`) : void handleUpgrade('trial_expiring')),
        }}
        onDismiss={
          canDismiss
            ? () => {
                writeDismissed(PHASE_DISMISS_KEY(phase), copy.dismissReshowHours)
                setTick(t => t + 1)
              }
            : undefined
        }
        className={className}
      />
    )
  }

  // 5–6. Monthly data points on a paid plan.
  if (!hasActiveSubscription || hasActiveTrial || eventLimit <= 0) return null
  const level = getQuotaLevel(eventsUsed, eventLimit)

  if (level === 'full') {
    const copy = buildQuotaInterruptCopy({
      limit: eventLimit,
      planTier: currentPlan,
      planName: displayPlanName,
      counts: {
        funnels: usage.funnels.current,
        heatmaps: usage.heatmaps.current,
        tests: usage.splitTests.current,
      },
    })
    // Zero-height anchor: the wash covers the content area below this slot
    // (SidebarInset clips it), leaving the header and tabs usable.
    return (
      <div className="relative z-30 h-0">
        <div className="absolute inset-x-0 top-0 flex h-[100svh] justify-center overflow-y-auto bg-white/55 px-4 pt-6 sm:pt-[72px] dark:bg-black/40">
          <EventQuotaInterrupt copy={copy} onCta={() => void openPortal()} className="h-fit" />
        </div>
      </div>
    )
  }

  if (level === 'warning') {
    const key = quotaDismissKey(new Date())
    if (readDismissed(key)) return null
    const copy = buildQuotaWarningCopy({ used: eventsUsed, limit: eventLimit })
    const next = currentPlan === 'scale' ? null : currentPlan === 'business' ? 'Scale' : 'Business'
    return (
      <UrgencyBanner
        tone="amber"
        chip={copy.chip}
        chipIcon={<ChipMeter ratio={eventsUsed / eventLimit} />}
        message={copy.message}
        cta={next ? { label: `Upgrade to ${next}`, onClick: () => void openPortal() } : undefined}
        onDismiss={() => dismiss(key)}
        className={className}
      />
    )
  }

  return null
}

export default TrialBanner
